import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { BASE } from "@/api/client";
import { diaryReportsKeys } from "@/api/diaryReportsKeys";
import { notificationsKeys } from "@/api/notificationsKeys";
import { supabase } from "@/lib/supabase";

type StreamNotificationPayload = {
  id: string;
  organizationId: string;
  userId: string;
  typeKey: string;
  params?: Record<string, unknown>;
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function readSseStream(
  body: ReadableStream<Uint8Array>,
  onNotification: (payload: StreamNotificationPayload) => void,
  signal: AbortSignal,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let eventName = "message";

  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let separator = buffer.indexOf("\n\n");
    while (separator >= 0) {
      const rawEvent = buffer.slice(0, separator);
      buffer = buffer.slice(separator + 2);

      const dataLines: string[] = [];
      for (const line of rawEvent.split("\n")) {
        if (line.startsWith("event:")) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice(5).trim());
        } else if (line.startsWith(":")) {
          /* comment / keepalive */
        }
      }

      if (eventName === "notification" && dataLines.length > 0) {
        try {
          const parsed = JSON.parse(dataLines.join("\n")) as StreamNotificationPayload;
          onNotification(parsed);
        } catch {
          /* ignore malformed chunk */
        }
      }

      eventName = "message";
      separator = buffer.indexOf("\n\n");
    }
  }
}

/**
 * Keeps a long-lived SSE connection and invalidates notification (and related
 * report) queries when the server pushes a new notification. No interval polling.
 *
 * Mount once under authenticated app chrome (see {@link NotificationStreamGate})
 * so AppShell ↔ FormFlowShell navigations do not reconnect the stream.
 */
export function useNotificationStream(enabled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !supabase) return;

    const controller = new AbortController();
    let cancelled = false;

    const onNotification = (payload: StreamNotificationPayload) => {
      void queryClient.invalidateQueries({ queryKey: notificationsKeys.list() });

      const projectId =
        typeof payload.params?.projectId === "string" ? payload.params.projectId : null;
      if (
        projectId &&
        (payload.typeKey === "projectReport.ready" || payload.typeKey === "projectReport.failed")
      ) {
        void queryClient.invalidateQueries({
          queryKey: diaryReportsKeys.project(projectId),
        });
      }
    };

    async function connect(): Promise<void> {
      let backoffMs = 1_000;

      while (!cancelled && !controller.signal.aborted) {
        try {
          const {
            data: { session },
          } = await supabase!.auth.getSession();
          const token = session?.access_token;
          if (!token) {
            await sleep(2_000);
            continue;
          }

          const response = await fetch(`${BASE}/v1/notifications/stream`, {
            method: "GET",
            headers: {
              Accept: "text/event-stream",
              Authorization: `Bearer ${token}`,
              "X-Version-Web": import.meta.env.VITE_GIT_SHA ?? "unknown",
            },
            signal: controller.signal,
          });

          if (!response.ok || !response.body) {
            throw new Error(`SSE failed: ${response.status}`);
          }

          backoffMs = 1_000;
          await readSseStream(response.body, onNotification, controller.signal);
        } catch (err) {
          if (cancelled || controller.signal.aborted) return;
          if (import.meta.env.DEV) {
            console.warn("[notifications] stream reconnecting", err);
          }
          await sleep(backoffMs);
          backoffMs = Math.min(backoffMs * 2, 15_000);
        }
      }
    }

    void connect();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enabled, queryClient]);
}
